import {
  Component,
  ElementRef,
  HostListener,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import { ActivatedRoute, ParamMap, Router } from '@angular/router';
import { combineLatest, map, skip, Subject, take, takeUntil, tap } from 'rxjs';
import {
  PrintAttributeListFilters,
  PrintAttributeService,
} from '../../../services/print-attribute.service';
import { Sort } from '@angular/material/sort';
import { SelectionModel } from '@angular/cdk/collections';
import {
  PrintAttributeDefinition,
  PrintAttributeType,
} from '../../../models/print-attribute.model';
import { PageEvent } from '@angular/material/paginator';
import { DEFAULT_SNACKBAR_CONFIG } from 'src/app/@shared/constants/site.constants';
import { MatSnackBar } from '@angular/material/snack-bar';
import { FormControl } from '@angular/forms';
import { ConfirmDialogComponent } from 'src/app/@shared';
import { MatDialog } from '@angular/material/dialog';
import { MatSidenav } from '@angular/material/sidenav';
import { DivisionService } from '../../../services/division.service';
import { EventTypeService } from '../../../services/event-type.service';
import { EventWorkflowService } from '../../../services/event-workflow.service';
import { AuthorizationService } from 'src/app/@shared/services/authorization.service';

@Component({
  selector: 'app-print-attributes-list-table',
  templateUrl: './print-attributes-list-table.component.html',
  styleUrls: ['./print-attributes-list-table.component.scss'],
})
export class PrintAttributesListTableComponent implements OnInit, OnDestroy {
  @ViewChild(MatSidenav) sidenav!: MatSidenav;
  @ViewChild('filterSidenav', { read: ElementRef }) filterSidenavElement?: ElementRef<HTMLElement>;
  @ViewChild('filterToggleButton', { read: ElementRef }) filterToggleButton?: ElementRef<HTMLElement>;
  private destroy$ = new Subject<void>();

  divisionFilterControl = new FormControl<string[]>([]);
  attributeTypeFilterControl = new FormControl<string[]>([]);
  eventTypeFilterControl = new FormControl<string[]>([]);

  divisionOptions: { id: string; name: string }[] = [];
  eventTypeOptions: { id: string; name: string }[] = [];
  attributeTypeOptions: { id: string; label: string }[] = [];

  viewModel$ = combineLatest([
    this.printAttributeService.printAttributes$,
    this.printAttributeService.page$,
    this.printAttributeService.totalRecords$,
  ]).pipe(
    tap(([attributes, page, totalRecords]) => {
      this.selection.clear();
    }),
    map(([attributes, page, totalRecords]) => ({
      attributes,
      page,
      totalRecords,
    })),
  );

  onFilterChange(): void {
    this.selection.clear();
  }

  toggleFilters(): void {
    this.sidenav?.toggle();
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    if (!this.sidenav?.opened) return;

    const target = event.target as HTMLElement | null;
    if (!target) return;

    if (this.filterSidenavElement?.nativeElement.contains(target)) return;
    if (this.filterToggleButton?.nativeElement.contains(target)) return;
    if (target.closest('.cdk-overlay-container')) return;

    this.sidenav.close();
  }

  getPrintAttributeTypeLabel(typeId: string): string {
    const type = this.attributeTypeOptions.find(t => t.id === typeId);
    return type ? type.label : typeId;
  }

  applyFilters(): void {
    const filters = this.readFiltersFromControls();
    this.printAttributeService.setListFilters(filters);
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: {
        divisionIds: filters.divisionIds.length ? filters.divisionIds : null,
        attributeTypes: filters.attributeTypes.length
          ? filters.attributeTypes
          : null,
        eventTypeIds: filters.eventTypeIds.length ? filters.eventTypeIds : null,
      },
      queryParamsHandling: 'merge',
      replaceUrl: true,
    });
    this.toggleFilters();
  }

  clearFilters(): void {
    this.patchFilterControls({
      divisionIds: [],
      attributeTypes: [],
      eventTypeIds: [],
    });
    this.printAttributeService.clearListFilters();
    this.selection.clear();
  }

  resetFilters(): void {
    this.clearFilters();
    this.printAttributeService.search('');
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: {
        divisionIds: null,
        attributeTypes: null,
        eventTypeIds: null,
      },
      queryParamsHandling: 'merge',
      replaceUrl: true,
    });
    this.toggleFilters();
  }

  printAttributes: PrintAttributeDefinition[] = [];
  selection = new SelectionModel<any>(true, []);
  displayedColumns = [
    'select',
    'Name',
    'DivisionNames',
    'EventTypeName',
    'PrintAttributeType',
    'Description',
    'BannerName',
    'Versions',
    'Actions',
  ];
  searchControl = new FormControl('');
  readOnlyMode = false;

  constructor(
    private printAttributeService: PrintAttributeService,
    private matSnackBar: MatSnackBar,
    private dialog: MatDialog,
    private divisionService: DivisionService<any>,
    private eventTypeService: EventTypeService<any>,
    private route: ActivatedRoute,
    private router: Router,
    private ewService: EventWorkflowService,
    private authorizationService: AuthorizationService
  ) {}

  ngOnInit(): void {
    this.resetFilters();
    this.printAttributeService.printAttributeTypes$.pipe(takeUntil(this.destroy$)).subscribe((types) => {
      this.attributeTypeOptions = types;
    });

    this.readOnlyMode = !this.authorizationService.checkAccess('edit||offermang');
    if (!this.canEdit()) {
      this.displayedColumns = this.displayedColumns.filter((column) => column !== 'Actions');
    }

    this.divisionService.divisionsList$
      .pipe(take(1), takeUntil(this.destroy$))
      .subscribe((divisions) => {
        this.divisionOptions = divisions.map((d) => ({
          id: d.Id,
          name: d.DivisionName,
        }));
      });

    this.eventTypeService.eventTypesList$
      .pipe(take(1), takeUntil(this.destroy$))
      .subscribe((types) => {
        this.eventTypeOptions = types.map((t) => ({
          id: t.Id,
          name: t.EventTypeName,
        }));
      });

    this.syncListRouteToState(this.route.snapshot.queryParamMap);

    this.route.queryParamMap
      .pipe(skip(1), takeUntil(this.destroy$))
      .subscribe((qp) => this.syncListRouteToState(qp));

    this.viewModel$.pipe(takeUntil(this.destroy$)).subscribe((vm) => {
      this.printAttributes = vm.attributes;
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private readFiltersFromControls(): PrintAttributeListFilters {
    return {
      divisionIds: [...(this.divisionFilterControl.value ?? [])],
      attributeTypes: [...(this.attributeTypeFilterControl.value ?? [])],
      eventTypeIds: [...(this.eventTypeFilterControl.value ?? [])],
    };
  }

  private patchFilterControls(f: PrintAttributeListFilters): void {
    this.divisionFilterControl.setValue([...f.divisionIds], {
      emitEvent: false,
    });
    this.attributeTypeFilterControl.setValue([...f.attributeTypes], {
      emitEvent: false,
    });
    this.eventTypeFilterControl.setValue([...f.eventTypeIds], {
      emitEvent: false,
    });
  }

  private syncListRouteToState(qp: ParamMap): void {
    const next: PrintAttributeListFilters = {
      divisionIds: qp.getAll('divisionIds'),
      attributeTypes: qp.getAll('attributeTypes'),
      eventTypeIds: qp.getAll('eventTypeIds'),
    };
    this.patchFilterControls(next);
    this.printAttributeService.setListFilters(next);
  }

  onSearch(event: Event): void {
    this.printAttributeService.search((event.target as HTMLInputElement).value);
  }

  clearSearch(): void {
    this.searchControl.setValue('');
    this.printAttributeService.search('');
    this.selection.clear();
  }

  onSort(sortState: Sort): void {
    this.printAttributeService.sort(sortState);
    this.selection.clear();
  }

  isAllSelected() {
    const numSelected = this.selection.selected.length;
    const numRows = this.printAttributes.length;
    return numSelected === numRows;
  }

  /** Selects all rows if they are not all selected; otherwise clear selection. */
  masterToggle() {
    this.isAllSelected()
      ? this.selection.clear()
      : this.printAttributes.forEach((row) => this.selection.select(row));
  }

  public getSelectedSectionRecords() {
    return this.selection.selected || [];
  }

  public clearSelection() {
    this.selection.clear();
  }

  onPage(pageEvent: PageEvent): void {
    this.selection.clear();
    this.printAttributeService.page(pageEvent);
  }

  deleteSelectedRecords(): void {
    const confirmDialog = this.dialog.open(ConfirmDialogComponent, {
      data: {
        title: 'Confirm Delete',
        message: `Do you want to remove Print Attribute(s)?`,
      },
      disableClose: true,
    });
    confirmDialog.afterClosed().subscribe((result) => {
      if (result === true) {
        const selectedIds = this.getSelectedSectionRecords().map(
          (record) => record.Detail.Id,
        );
        this.printAttributeService.deletePrintAttributes(selectedIds).subscribe(() => {
          this.clearSelection();
          this.printAttributeService.reload();
          this.matSnackBar.open(
            `Print Attribute(s) deleted`,
            'OK',
            DEFAULT_SNACKBAR_CONFIG,
          );
        });
      }
    });
  }

  cloneSelectedRecords(): void {
    const selectedIds = this.getSelectedSectionRecords().map(
      (record) => record.DomainId,
    );
    this.printAttributeService.clonePrintAttributes(selectedIds).subscribe(() => {
      this.clearSelection();
      this.printAttributeService.reload();
      this.matSnackBar.open(
        `Print Attribute(s) cloned`,
        'OK',
        DEFAULT_SNACKBAR_CONFIG,
      );
    });
  }

  downloadCSV(): void {
    this.printAttributeService.downloadPrintAttributesCSV();
  }

  canEdit(): boolean {
    return !this.readOnlyMode;
  }
}
